/**
 * Charts as server-rendered SVG.
 *
 * Scope §5.5 commits to SSR SVG specifically to remove the legacy chart-to-image pass
 * (F5), where charts were rendered in a browser, screenshotted, and pasted back in.
 *
 * These are hand-rolled rather than drawn with a charting library, for three reasons
 * that all reduce to R4:
 *
 *   1. Determinism. Every coordinate here is rounded to a fixed number of decimals
 *      before it reaches the DOM. Libraries emit full float precision, which can
 *      differ in the last digit across platforms and would break byte-identical PDFs.
 *   2. No generated ids. Charting libraries commonly emit `clipPath` ids from a
 *      counter or a random seed; either makes two renders differ.
 *   3. No runtime. The render worker disables network and these must draw in one
 *      synchronous SSR pass, which rules out anything that measures in a browser.
 *
 * The trade is that these are simple charts. They are exactly as simple as the three
 * the legacy email report draws, and no simpler.
 */

import * as React from 'react';
import { formatInt, formatPct } from './format.js';

/** Fixed decimal places for every emitted coordinate. See note 1 above. */
const P = 2;
const r = (n: number): number => Number(n.toFixed(P));

/**
 * Series colours.
 *
 * The first two are the theme's brand colours as CSS custom properties, so a chart follows the
 * report's chosen `themeColor` (a single-series bar chart is drawn entirely in `--color-primary`).
 * They resolve to navy `#001345` / accent `#06CDF2` by default. The rest are fixed hues chosen to
 * stay distinguishable in greyscale, because these reports are routinely printed.
 */
export const CHART_COLORS = ['var(--color-primary)', 'var(--color-accent)', '#5B8DEF', '#8A93A0', '#00A38B', '#C2410C'];
/*
 * Pie slices need more distinct hues than a few chart series: with the 6-colour series palette a pie of
 * 8 apps + Other repeated colours (Plex and VUit both navy), so slices could not be told apart. Ten
 * distinct colours for named slices; "Other" always takes a neutral grey so it reads as the remainder.
 */
export const PIE_COLORS = [
  'var(--color-primary)', 'var(--color-accent)', '#5B8DEF', '#00A38B', '#C2410C',
  '#7C3AED', '#D4A017', '#DB2777', '#4D7C0F', '#0E7490',
];
const PIE_OTHER_COLOR = '#B8C1CC';

const fmt = (v: number, kind: 'int' | 'pct') => (kind === 'pct' ? formatPct(v, 1) : formatInt(v));

export interface Series { name?: string; values: Array<number | null> }

/**
 * Pick axis ticks that land on round numbers.
 *
 * Without this the axis reads `0 / 3847 / 7694`, which is technically correct and
 * useless to a reader.
 */
function niceTicks(max: number, count = 4): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const raw = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  const ticks: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) ticks.push(r(v));
  // The top tick must be at or above `max`, or bars/points taller than the last gridline overflow the plot
  // area (a 12.89% bar against a top tick of 10 rendered at 129% height, into the chart title). When `max`
  // is not a multiple of `step` the loop stops one tick short — add the ceiling tick so there is headroom.
  if (ticks[ticks.length - 1] < max) ticks.push(r(ticks[ticks.length - 1] + step));
  return ticks;
}

// ---------------------------------------------------------------------------

export interface SeriesChartProps {
  categories: string[];
  series: Series[];
  chartType: 'bar' | 'line';
  heightIn: number;
  xLabel?: string;
  yLabel?: string;
  valueFormat: 'int' | 'pct';
  showLegend: boolean;
  /** Categories dropped beyond the cap, disclosed rather than hidden. */
  omitted: number;
}

// See the note in Document.tsx: the React import is a runtime requirement of the
// classic JSX transform, so it is referenced in a signature rather than tidied away.
export function SeriesChartSvg(p: SeriesChartProps): React.ReactElement {
  // A fixed viewBox in abstract units; CSS scales it to the printed size. Keeping the
  // geometry independent of the physical size is what lets the same numbers round
  // identically regardless of page dimensions.
  const W = 720;
  const H = Math.round(p.heightIn * 96);
  const padL = 56, padR = 12, padT = 12, padB = p.xLabel ? 44 : 32;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const all = p.series.flatMap((s) => s.values.filter((v): v is number => typeof v === 'number'));
  const max = all.length ? Math.max(...all) : 0;
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1] || 1;

  const y = (v: number) => r(padT + plotH - (v / top) * plotH);
  const bandW = p.categories.length > 0 ? plotW / p.categories.length : plotW;
  const bandCentre = (i: number) => r(padL + bandW * i + bandW / 2);

  return (
    <figure style={{ margin: 0 }}>
      <svg data-chart="1"
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={`${p.heightIn}in`}
        role="img"
        style={{ display: 'block', overflow: 'visible' }}
      >
        {/* gridlines + y axis labels */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} y1={y(t)} x2={r(padL + plotW)} y2={y(t)} stroke="#E8E8E8" strokeWidth={1} />
            <text x={padL - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="#5A6472">{fmt(t, p.valueFormat)}</text>
          </g>
        ))}

        {p.chartType === 'bar'
          ? p.series.map((s, si) => {
              // Grouped bars: each series gets a slot inside the category band.
              const inner = bandW * 0.7;
              const barW = p.series.length > 0 ? inner / p.series.length : inner;
              return (
                <g key={si}>
                  {s.values.map((v, i) => {
                    if (typeof v !== 'number') return null;
                    const x = r(padL + bandW * i + (bandW - inner) / 2 + barW * si);
                    const h = r(padT + plotH - y(v));
                    return <rect key={i} x={x} y={y(v)} width={r(barW)} height={h} fill={CHART_COLORS[si % CHART_COLORS.length]} />;
                  })}
                </g>
              );
            })
          : p.series.map((s, si) => {
              const pts = s.values
                .map((v, i) => (typeof v === 'number' ? `${bandCentre(i)},${y(v)}` : null))
                .filter((x): x is string => x !== null);
              if (pts.length === 0) return null;
              return (
                <g key={si}>
                  <polyline
                    points={pts.join(' ')}
                    fill="none"
                    stroke={CHART_COLORS[si % CHART_COLORS.length]}
                    strokeWidth={2}
                    strokeLinejoin="round"
                  />
                  {s.values.map((v, i) =>
                    typeof v === 'number'
                      ? <circle key={i} cx={bandCentre(i)} cy={y(v)} r={3} fill={CHART_COLORS[si % CHART_COLORS.length]} />
                      : null
                  )}
                </g>
              );
            })}

        {/* baseline */}
        <line x1={padL} y1={y(0)} x2={r(padL + plotW)} y2={y(0)} stroke="#8A93A0" strokeWidth={1} />

        {/* category labels — thinned rather than overlapped when crowded */}
        {p.categories.map((c, i) => {
          const every = Math.ceil(p.categories.length / 12);
          if (i % every !== 0) return null;
          return (
            <text key={i} x={bandCentre(i)} y={H - (p.xLabel ? 30 : 14)} textAnchor="middle" fontSize={11} fill="#5A6472">
              {c}
            </text>
          );
        })}

        {p.xLabel && <text x={r(padL + plotW / 2)} y={H - 8} textAnchor="middle" fontSize={11} fill="#0A0A0A">{p.xLabel}</text>}
        {p.yLabel && (
          <text x={14} y={r(padT + plotH / 2)} textAnchor="middle" fontSize={11} fill="#0A0A0A"
                transform={`rotate(-90 14 ${r(padT + plotH / 2)})`}>{p.yLabel}</text>
        )}
      </svg>

      {p.showLegend && p.series.some((s) => s.name) && (
        <div className="tiny" style={{ display: 'flex', gap: '0.14in', justifyContent: 'center', marginTop: '0.04in' }}>
          {p.series.map((s, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.04in' }}>
              <span style={{ width: '0.09in', height: '0.09in', background: CHART_COLORS[i % CHART_COLORS.length], display: 'inline-block' }} />
              {s.name}
            </span>
          ))}
        </div>
      )}
      {p.omitted > 0 && (
        <figcaption className="tiny muted" style={{ textAlign: 'center' }}>
          Showing {formatInt(p.categories.length)} of {formatInt(p.categories.length + p.omitted)} categories.
        </figcaption>
      )}
    </figure>
  );
}

// ---------------------------------------------------------------------------

export interface PieChartProps {
  slices: Array<{ label: string; value: number }>;
  heightIn: number;
  showLegend: boolean;
  /** Value folded into "Other". Zero when nothing was folded. */
  otherValue: number;
  otherCount: number;
}

export function PieChartSvg(p: PieChartProps) {
  const size = Math.round(p.heightIn * 96);
  const cx = size / 2, cy = size / 2, radius = size / 2 - 4;

  const slices = p.otherValue > 0
    ? [...p.slices, { label: p.otherCount > 0 ? `Other (${p.otherCount})` : 'Other', value: p.otherValue }]
    : p.slices;
  const otherIdx = p.otherValue > 0 ? slices.length - 1 : -1;
  const colorAt = (i: number) => (i === otherIdx ? PIE_OTHER_COLOR : PIE_COLORS[i % PIE_COLORS.length]);
  const total = slices.reduce((t, s) => t + s.value, 0);

  let angle = -Math.PI / 2; // start at 12 o'clock
  const paths = slices.map((s, i) => {
    const sweep = total > 0 ? (s.value / total) * Math.PI * 2 : 0;
    const x1 = r(cx + radius * Math.cos(angle));
    const y1 = r(cy + radius * Math.sin(angle));
    angle += sweep;
    const x2 = r(cx + radius * Math.cos(angle));
    const y2 = r(cy + radius * Math.sin(angle));
    const large = sweep > Math.PI ? 1 : 0;
    // A single slice covering the whole circle degenerates: the arc's start and end
    // points coincide and the path renders as nothing. Draw it as a circle instead.
    const d = slices.length === 1 || sweep >= Math.PI * 2 - 1e-9
      ? null
      : `M ${r(cx)} ${r(cy)} L ${x1} ${y1} A ${r(radius)} ${r(radius)} 0 ${large} 1 ${x2} ${y2} Z`;
    const fill = colorAt(i);
    return d === null
      ? <circle key={i} cx={r(cx)} cy={r(cy)} r={r(radius)} fill={fill} />
      : <path key={i} d={d} fill={fill} />;
  });

  return (
    <figure style={{ margin: 0, display: 'flex', gap: '0.14in', alignItems: 'center' }}>
      <svg data-chart="1" viewBox={`0 0 ${size} ${size}`} width={`${p.heightIn}in`} height={`${p.heightIn}in`} role="img" style={{ flex: '0 0 auto' }}>
        {paths}
      </svg>
      {p.showLegend && (
        // `min-width: 0` on the list lets it shrink inside a narrow (e.g. half-width) column instead
        // of pushing the value past the card edge; the label wraps and the value stays put.
        <ul className="plain tiny" style={{ listStyle: 'none', paddingLeft: 0, margin: 0, minWidth: 0, flex: '1 1 auto' }}>
          {slices.map((s, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '0.05in' }}>
              <span style={{ width: '0.08in', height: '0.08in', background: colorAt(i), display: 'inline-block', flex: '0 0 auto', alignSelf: 'center' }} />
              <span style={{ flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>{s.label}</span>
              <span className="muted num" style={{ flex: '0 0 auto' }}>{formatInt(s.value)}</span>
            </li>
          ))}
        </ul>
      )}
    </figure>
  );
}
